import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import vendorApi from '../../services/vendorApi';
import SearchableDropdown from '../../components/ui/SearchableDropdown';
import BrandModal from '../../components/ui/modals/BrandModal';
import CategoryModal from '../../components/ui/modals/CategoryModal';

const EditVendorProductPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState({
    name: '',
    description: '',
    regularPrice: '',
    specialOfferPrice: '',
    stock: 0,
    brand: '',
    categories: [],
    status: 'active'
  });
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Modal state
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [brandError, setBrandError] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [creatingBrand, setCreatingBrand] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);

  // Build flat category list for display
  function flattenCategories(tree, prefix = '') {
    const result = [];
    tree.forEach((node) => {
      const label = `${prefix}${node.name}`;
      result.push({ _id: node._id, value: node._id, label });
      if (node.children && node.children.length > 0) {
        result.push(...flattenCategories(node.children, `${prefix}— `));
      }
    });
    return result;
  }

  function buildTree(cats) {
    const byParent = new Map();
    cats.forEach(c => {
      const pid = c.parentId ? (c.parentId._id || c.parentId) : null;
      if (!byParent.has(pid)) byParent.set(pid, []);
      byParent.get(pid).push({ ...c, children: [] });
    });
    const build = (pid) => (byParent.get(pid) || []).map(node => ({
      ...node,
      children: build(node._id)
    }));
    return build(null);
  }

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [brandsRes, categoriesRes, productRes] = await Promise.all([
          vendorApi.get('/brands'),
          vendorApi.get('/categories'),
          vendorApi.get(`/products/vendor/${id}`),
        ]);
        setBrands(brandsRes.data?.data || []);
        const cats = categoriesRes.data?.data || [];
        setCategories(cats);
        setCategoryTree(buildTree(cats));
        const p = productRes.data?.data;
        if (p) {
          setForm({
            name: p.name || '',
            description: p.description || '',
            regularPrice: p.regularPrice || '',
            specialOfferPrice: p.specialOfferPrice || '',
            stock: p.stock || 0,
            brand: p.brand?._id || p.brand || '',
            categories: (p.categories || []).map((c) => c._id || c),
            status: p.status || 'active'
          });
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load product data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  const handleCreateBrandInline = async (brandForm) => {
    if (!brandForm.name?.trim()) { setBrandError('Please enter a brand name'); return; }
    const exists = brands.some(b => b.name.toLowerCase() === brandForm.name.trim().toLowerCase());
    if (exists) { setBrandError('Brand with this name already exists'); return; }
    try {
      setCreatingBrand(true);
      setBrandError('');
      const res = await vendorApi.post('/brands/vendor', {
        name: brandForm.name.trim(),
        description: brandForm.description || '',
        picture: brandForm.picture || '',
        tags: brandForm.tags || [],
        metaTitle: brandForm.metaTitle || '',
        metaDescription: brandForm.metaDescription || '',
        keywords: brandForm.keywords || '',
        status: brandForm.status || 'active'
      });
      if (res.data?.success) {
        const created = res.data.data;
        setBrands(prev => [...prev, created]);
        setForm(f => ({ ...f, brand: created._id }));
        setIsBrandModalOpen(false);
        toast.success(`Brand "${created.name}" created and selected!`);
      } else {
        setBrandError(res.data?.message || 'Failed to create brand');
      }
    } catch (err) {
      setBrandError(err.response?.data?.message || err.response?.data?.error || 'Failed to create brand');
    } finally {
      setCreatingBrand(false);
    }
  };

  const handleCreateCategoryInline = async (catForm) => {
    if (!catForm.name?.trim()) { setCategoryError('Please enter a category name'); return; }
    const exists = categories.some(c => c.name.toLowerCase() === catForm.name.trim().toLowerCase());
    if (exists) { setCategoryError('Category with this name already exists'); return; }
    try {
      setCreatingCategory(true);
      setCategoryError('');
      const payload = { name: catForm.name.trim(), status: 'active' };
      if (catForm.parentId) payload.parentId = catForm.parentId;
      const res = await vendorApi.post('/categories/vendor', payload);
      if (res.data?.success) {
        const created = res.data.data;
        const updatedCats = [...categories, created];
        setCategories(updatedCats);
        setCategoryTree(buildTree(updatedCats));
        setForm(f => ({ ...f, categories: [...(f.categories || []), created._id] }));
        setIsCategoryModalOpen(false);
        toast.success(`Category "${created.name}" created and selected!`);
        return true;
      }
      return false;
    } catch (err) {
      setCategoryError(err.response?.data?.message || err.response?.data?.error || 'Failed to create category');
      return false;
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        name: form.name,
        description: form.description,
        regularPrice: Number(form.regularPrice),
        specialOfferPrice: form.specialOfferPrice ? Number(form.specialOfferPrice) : undefined,
        stock: Number(form.stock),
        brand: form.brand,
        categories: form.categories,
        status: form.status,
      };
      const res = await vendorApi.put(`/products/${id}`, payload);
      if (res.data?.success) {
        toast.success('Product updated');
        navigate('/vendor/products');
      } else {
        toast.error(res.data?.message || 'Failed to update');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  const flatCategories = flattenCategories(categoryTree);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Product</h1>
          <p className="mt-1 text-sm text-gray-500">Update your product details</p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Regular Price</label>
              <input
                type="number"
                min="0"
                value={form.regularPrice}
                onChange={(e) => setForm({ ...form, regularPrice: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Special Price (optional)</label>
              <input
                type="number"
                min="0"
                value={form.specialOfferPrice}
                onChange={(e) => setForm({ ...form, specialOfferPrice: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Stock</label>
              <input
                type="number"
                min="0"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Brand */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Brand</label>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1">
                  <SearchableDropdown
                    options={brands.map(b => ({ label: b.name, value: b._id }))}
                    value={form.brand}
                    onChange={(value) => setForm(f => ({ ...f, brand: value }))}
                    placeholder="Select brand"
                    className="w-full"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => { setBrandError(''); setIsBrandModalOpen(true); }}
                  className="flex-shrink-0 px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm"
                >+ New</button>
              </div>
              {brandError && (
                <div className="mt-2 rounded-md bg-red-50 p-3 border border-red-200">
                  <p className="text-sm text-red-700">{brandError}</p>
                </div>
              )}
            </div>

            {/* Categories */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Categories</label>
              <div className="space-y-2 mt-1">
                {/* Selected category chips */}
                {form.categories.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {form.categories.map(catId => {
                      const cat = categories.find(c => c._id === catId);
                      return cat ? (
                        <span key={catId} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-primary-100 text-primary-800">
                          {cat.name}
                          <button type="button" onClick={() => setForm(f => ({ ...f, categories: f.categories.filter(id => id !== catId) }))} className="hover:text-primary-600">×</button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <SearchableDropdown
                      options={flatCategories.filter(c => !form.categories.includes(c._id))}
                      value=""
                      onChange={(value) => { 
                        if (value && !form.categories.includes(value)) { 
                          const getRoot = (catId) => {
                            let current = categories.find(c => c._id === catId);
                            while (current && current.parentId) {
                              const pId = typeof current.parentId === 'object' ? current.parentId._id : current.parentId;
                              const parent = categories.find(c => c._id === pId);
                              if (!parent) break;
                              current = parent;
                            }
                            return current ? current._id : null;
                          };
                          
                          setForm(f => {
                            let currentSelections = [...f.categories];
                            if (currentSelections.length > 0) {
                              const existingRoot = getRoot(currentSelections[0]);
                              const newRoot = getRoot(value);
                              if (existingRoot && newRoot && existingRoot !== newRoot) {
                                currentSelections = [];
                              }
                            }
                            return { ...f, categories: [...currentSelections, value] };
                          });
                        } 
                      }}
                      placeholder="Add a category..."
                      className="w-full"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => { setCategoryError(''); setIsCategoryModalOpen(true); }}
                    className="flex-shrink-0 px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm"
                  >+ New</button>
                </div>
                {categoryError && (
                  <div className="mt-2 rounded-md bg-red-50 p-3 border border-red-200">
                    <p className="text-sm text-red-700">{categoryError}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={5}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Link to="/vendor/products" className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md">Cancel</Link>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-md disabled:opacity-50">
              {saving ? 'Saving...' : 'Update Product'}
            </button>
          </div>
        </form>
      </div>

      {/* Inline modals */}
      <BrandModal
        open={isBrandModalOpen}
        onClose={() => setIsBrandModalOpen(false)}
        onCreate={handleCreateBrandInline}
        error={brandError}
        setError={setBrandError}
        creating={creatingBrand}
        brandOptions={brands}
      />
      <CategoryModal
        open={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        onCreate={handleCreateCategoryInline}
        parentOptions={flatCategories}
        error={categoryError}
        setError={setCategoryError}
        creating={creatingCategory}
      />
    </div>
  );
};

export default EditVendorProductPage;
